export interface Task {
  name: string;
  status: string;
  data: string;
  comments: string;
  customStatusOptions?: string[];
  
  // Audit trail for DATA column
  dataLastEditedBy?: string;      // User nickname (e.g., "JSM")
  dataLastEditedAt?: string;      // ISO date string
  
  // Audit trail for STATUS column
  statusLastEditedBy?: string;    // User nickname
  statusLastEditedAt?: string;    // ISO date string
}

export interface Contract {
  id: string;
  contractNumber: string;
  product: string;
  transportationVehicle: string;
  year: number;
  status: string;
  progress: number;
  tasks: Task[];
  createdAt: string;
  updatedAt?: string;
  companyId?: string; // Company this contract belongs to
  userId?: string; // User who created the contract (Firebase UID)
  
  // Contract creation audit
  createdBy?: string; // User nickname (e.g., "JSM")
  createdByName?: string; // Full name (e.g., "John Smith")
  createdByUserId?: string; // Firebase UID for backwards compatibility
  
  assignedTo?: string; // User ID assigned to this contract
  tags?: string[];
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  estimatedCompletion?: string;
}

export interface UserInfo {
  company: string;
  name: string;
  id: string;
}

export interface FilterState {
  contract: string;
  product: string;
  transportation: string;
  year: string;
  status: string;
}

export const DEFAULT_TASK_TEMPLATE: Task[] = [
  { name: 'Transaction Type', status: 'Booked', data: '', comments: '' },
  { name: 'Purchase Contract No and Date', status: 'Booked', data: '', comments: '' },
  { name: 'Sale Contract No and Date', status: 'Pending', data: '', comments: '' },
  { name: 'Product', status: 'Booked', data: '', comments: '' },
  { name: 'Contractual Quantity/BL', status: 'Booked', data: '', comments: '' },
  { name: 'POL, Incoterms', status: 'Confirmed', data: '', comments: '' },
  { name: 'POD, Incoterms', status: 'Confirmed', data: '', comments: '' },
  { name: 'Contractual Latest Delivery Date', status: 'Pending', data: '', comments: '' },
  { name: 'Load Terminal Lay-Can', status: 'Pending', data: '', comments: '' },
  { name: 'Disch.Terminal Berth Slot', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel, IMO', status: 'To Be Nominated', data: '', comments: '', customStatusOptions: ['Pending','Presented','Confirmed','In progress','Cancelled','Completed','Booked','Nominated','Received','Sent','To Be Nominated'] },
  { name: 'CRM Transaction No', status: 'Pending', data: '', comments: '' },
  { name: 'POL ETA', status: 'Pending', data: '', comments: '' },
  { name: 'POL ETB', status: 'Pending', data: '', comments: '' },
  { name: 'POL ETC', status: 'Pending', data: '', comments: '' },
  { name: 'POD ETA', status: 'Pending', data: '', comments: '' },
  { name: 'POD ETB', status: 'Pending', data: '', comments: '' },
  { name: 'POD ETC', status: 'Pending', data: '', comments: '' },
  { name: "Vessel's Status", status: 'Loaded, on the way to POD', data: '', comments: '' },
  { name: 'BL Date&Weight', status: 'Pending', data: '', comments: '' },
  { name: 'Purch Order CRM', status: 'Booked', data: '', comments: '' },
  { name: 'Documents for Trade financing', status: 'Presented', data: '', comments: '' },
  { name: 'Confirmation POL/Vessel Nomination', status: 'Yes', data: '', comments: '' },
  { name: 'Confirmation POD/Vessel Nomination', status: 'Yes', data: '', comments: '', customStatusOptions: ['Pending','Presented','Confirmed','In progress','Cancelled','Completed','Booked','Nominated','Received','Sent','To Be Advised'] },
  { name: 'Ship Instructions', status: 'Presented', data: '', comments: '' },
  { name: 'Surveyor Nomination POL', status: 'Yes', data: '', comments: '' },
  { name: 'Cargo Insurance at Voyage', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo FCR/SC release', status: 'Pending', data: '', comments: '' },
  { name: 'Drafts of Ship Documents', status: 'Confirmed', data: '', comments: '' },
  { name: '(Copies of) Original ShipDocs', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Note CRM', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo in transit CRM', status: 'Pending', data: '', comments: '' },
  { name: 'Documents for Customs Clearance', status: 'Presented', data: '', comments: '' },
  { name: 'Invoices for Customs Clearance/Duties', status: 'Paid', data: '', comments: '', customStatusOptions: ['Pending','Presented','Confirmed','In progress','Cancelled','Completed','Booked','Nominated','Received','Sent','Paid'] },
  { name: 'Surveyor Nomination POD', status: 'Nominated', data: '', comments: '' },
  { name: 'Contractual Invoice Amount', status: 'Received', data: '', comments: '' },
  { name: 'Discharge Confirmation', status: 'Presented', data: '', comments: '' },
  { name: 'Bank endorsed BL', status: 'Pending', data: '', comments: '' },
  { name: 'LOI for discharge without original BL', status: 'Pending', data: '', comments: '' },
  { name: 'Storage Certificate for Bank', status: 'Pending', data: '', comments: '' },
  { name: 'Warehouse CRM', status: 'Pending', data: '', comments: '' },
  { name: 'Demmurage calculation & Invoice', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Invoice', status: 'Pending', data: '', comments: '' },
  { name: 'Dispatch calculation & Invoice', status: 'Pending', data: '', comments: '' }
];

export const DEFAULT_STATUS_OPTIONS = [
  'Pending', 'Presented', 'Confirmed', 'In progress', 'Cancelled', 
  'Completed', 'Booked', 'Nominated', 'Received', 'Sent', 'Yes', 'No'
];

export const DEFAULT_TRANSPORTATION_OPTIONS = [
  'Vessel', 'Container', 'Truck', 'Air', 'Multimodal', 'Custom'
];

// Amazon FBA - Full Trade Cycle Template
export const AMAZON_FBA_TEMPLATE: Task[] = [
  { name: 'Purchase Order Issued to Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Purchase Order Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Proforma Invoice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Sent (Deposit/Full)', status: 'Pending', data: '', comments: '' },
  { name: 'Production Started', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Control Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Production Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Invoice Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Forwarder Booked', status: 'Pending', data: '', comments: '' },
  { name: 'Goods Loaded to Container', status: 'Pending', data: '', comments: '' },
  { name: 'Container Shipped from Factory', status: 'Pending', data: '', comments: '' },
  { name: 'Container at Origin Port', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loaded on Vessel', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departed', status: 'Pending', data: '', comments: '' },
  { name: 'In Transit - Sea Freight', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival at Destination Port', status: 'Pending', data: '', comments: '' },
  { name: 'Container Discharged', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Clearance Documents Submitted', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Clearance Approved', status: 'Pending', data: '', comments: '' },
  { name: 'Container Picked Up from Port', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery to Amazon Warehouse', status: 'Pending', data: '', comments: '' },
  { name: 'Amazon Receiving Started', status: 'Pending', data: '', comments: '' },
  { name: 'Amazon Inventory Available', status: 'Pending', data: '', comments: '' },
  { name: 'Final Payment to Supplier', status: 'Pending', data: '', comments: '' }
];

// Shopify Store - Direct Import Template
export const SHOPIFY_DIRECT_IMPORT_TEMPLATE: Task[] = [
  { name: 'Product Order Placed with Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Order Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Processed', status: 'Pending', data: '', comments: '' },
  { name: 'Production/Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Packing & Labeling', status: 'Pending', data: '', comments: '' },
  { name: 'Shipped to Freight Forwarder', status: 'Pending', data: '', comments: '' },
  { name: 'Consolidated at Freight Forwarder', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Shipment Method Selected', status: 'Pending', data: '', comments: '' },
  { name: 'Container/Air Freight Booked', status: 'Pending', data: '', comments: '' },
  { name: 'Shipped from Origin', status: 'Pending', data: '', comments: '' },
  { name: 'In Transit', status: 'Pending', data: '', comments: '' },
  { name: 'Arrival at Destination Country', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Documentation Submitted', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Cleared', status: 'Pending', data: '', comments: '' },
  { name: 'Delivered to 3PL/Warehouse', status: 'Pending', data: '', comments: '' },
  { name: 'Inventory Received & Counted', status: 'Pending', data: '', comments: '' },
  { name: 'Products Listed on Store', status: 'Pending', data: '', comments: '' },
  { name: 'Ready for Customer Orders', status: 'Pending', data: '', comments: '' }
];

// Dropshipping - Per Order Execution Template
export const DROPSHIPPING_PER_ORDER_TEMPLATE: Task[] = [
  { name: 'Customer Places Order', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Received', status: 'Pending', data: '', comments: '' },
  { name: 'Order Sent to Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Supplier Confirms Order', status: 'Pending', data: '', comments: '' },
  { name: 'Payment to Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Order Processed by Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Product Packed', status: 'Pending', data: '', comments: '' },
  { name: 'Shipped from Supplier', status: 'Pending', data: '', comments: '' },
  { name: 'Tracking Uploaded to Store', status: 'Pending', data: '', comments: '' },
  { name: 'In Transit', status: 'Pending', data: '', comments: '' },
  { name: 'Out for Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Delivered to Customer', status: 'Pending', data: '', comments: '' },
  { name: 'Follow-up Email Sent', status: 'Pending', data: '', comments: '' }
];

// Port Agent - Vessel Call (General Cargo) Template
export const PORT_AGENT_VESSEL_CALL_TEMPLATE: Task[] = [
  { name: 'Pre-Arrival Notice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Berth Nomination Request', status: 'Pending', data: '', comments: '' },
  { name: 'Port Authority Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Berth Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Tugboat Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Mooring Boat Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Health Authority Pre-Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Immigration Pre-Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Pre-Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel ETA Confirmation (24 hours)', status: 'Pending', data: '', comments: '' },
  { name: 'Shore Labor Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival at Anchorage', status: 'Pending', data: '', comments: '' },
  { name: 'Free Pratique Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Immigration Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Boards Vessel', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Entry to Port', status: 'Pending', data: '', comments: '' },
  { name: 'Tugboat Assistance', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Berthing', status: 'Pending', data: '', comments: '' },
  { name: 'Gangway Positioned', status: 'Pending', data: '', comments: '' },
  { name: 'Port State Control (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Operations Start', status: 'Pending', data: '', comments: '' },
  { name: 'Daily Cargo Progress Reports', status: 'Pending', data: '', comments: '' },
  { name: 'Bunker Fuel Delivery (if ordered)', status: 'Pending', data: '', comments: '' },
  { name: 'Fresh Water Supply (if ordered)', status: 'Pending', data: '', comments: '' },
  { name: 'Provisions Delivery (if ordered)', status: 'Pending', data: '', comments: '' },
  { name: 'Waste Disposal (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Ship Repairs/Surveys (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Crew Change Processing (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Operations Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Port Dues Calculation', status: 'Pending', data: '', comments: '' },
  { name: 'Final Statement of Facts', status: 'Pending', data: '', comments: '' },
  { name: 'Port Charges Payment', status: 'Pending', data: '', comments: '' },
  { name: 'Clearance Certificate Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Booking for Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Tugboat Booking for Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Departure Clearance from Authorities', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Boards for Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Unberthing', status: 'Pending', data: '', comments: '' },
  { name: 'Tugboat Assistance Out', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Disembarks', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Final Disbursement Account (FDA)', status: 'Pending', data: '', comments: '' },
  { name: 'Documents Package to Owner/Charterer', status: 'Pending', data: '', comments: '' }
];

// Port Agent - Container Vessel Call Template
export const PORT_AGENT_CONTAINER_VESSEL_TEMPLATE: Task[] = [
  { name: 'Pre-Arrival Notice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Container Manifest Received', status: 'Pending', data: '', comments: '' },
  { name: 'Berth Booking at Container Terminal', status: 'Pending', data: '', comments: '' },
  { name: 'Terminal Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot & Tugs Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Port Authority Clearances', status: 'Pending', data: '', comments: '' },
  { name: 'ETA Update (24 hours)', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Immigration/Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Boards', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Berthing', status: 'Pending', data: '', comments: '' },
  { name: 'Container Discharge Operations Start', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loading Operations Start', status: 'Pending', data: '', comments: '' },
  { name: 'Reefer Container Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Dangerous Cargo Handling (if any)', status: 'Pending', data: '', comments: '' },
  { name: 'Container Operations Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Hatch Covers Secured', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Manifests Updated', status: 'Pending', data: '', comments: '' },
  { name: 'Port Charges Settlement', status: 'Pending', data: '', comments: '' },
  { name: 'Departure Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot & Tugs for Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Final Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Disbursement Account', status: 'Pending', data: '', comments: '' }
];

// Port Agent - Tanker Vessel Call Template
export const PORT_AGENT_TANKER_VESSEL_TEMPLATE: Task[] = [
  { name: 'Pre-Arrival Notice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Details Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Terminal Slot Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Port Authority Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Oil Terminal Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot & Tugs Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Safety Inspection Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival at Anchorage', status: 'Pending', data: '', comments: '' },
  { name: 'Port Clearances Obtained', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Boards Vessel', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Berthing at Oil Terminal', status: 'Pending', data: '', comments: '' },
  { name: 'Pre-Loading/Discharge Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Ship-Shore Safety Checklist', status: 'Pending', data: '', comments: '' },
  { name: 'Hoses/Arms Connection', status: 'Pending', data: '', comments: '' },
  { name: 'Loading/Discharge Operations Start', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Operation Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Loading/Discharge Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Hoses/Arms Disconnection', status: 'Pending', data: '', comments: '' },
  { name: 'Line Flushing (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Inspection (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Documentation Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Certificate of Quantity/Quality', status: 'Pending', data: '', comments: '' },
  { name: 'Port Charges Calculation', status: 'Pending', data: '', comments: '' },
  { name: 'Clearance Certificate', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot & Tugs for Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Final Disbursement Account', status: 'Pending', data: '', comments: '' },
  { name: 'Documents to Parties', status: 'Pending', data: '', comments: '' }
];

// Port Agent - Bulk Carrier Call Template
export const PORT_AGENT_BULK_CARRIER_TEMPLATE: Task[] = [
  { name: 'Pre-Arrival Notice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Details & Quantity Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Berth Nomination', status: 'Pending', data: '', comments: '' },
  { name: 'Stevedore Company Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Survey Company Appointed', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot & Tugs Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Port Clearances', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Authorities Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Pilot Boards', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Berthing', status: 'Pending', data: '', comments: '' },
  { name: 'Draft Survey - Initial', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Loading/Discharge Operations Start', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Sampling (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Daily Progress Reports', status: 'Pending', data: '', comments: '' },
  { name: 'Weather Delays Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Loading/Discharge Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Draft Survey - Final', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Cleaning (if required)', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Inspection - Final', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Signing', status: 'Pending', data: '', comments: '' },
  { name: 'Statement of Facts Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Time Sheet Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Port Charges Settlement', status: 'Pending', data: '', comments: '' },
  { name: 'Clearance Certificate', status: 'Pending', data: '', comments: '' },
  { name: 'Departure Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Final Disbursement Account', status: 'Pending', data: '', comments: '' },
  { name: 'Laytime Calculation Report', status: 'Pending', data: '', comments: '' }
];

// Amazon Wholesale - Domestic Distribution Template
export const AMAZON_WHOLESALE_DOMESTIC_TEMPLATE: Task[] = [
  { name: 'Purchase Order to Distributor', status: 'Pending', data: '', comments: '' },
  { name: 'PO Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Terms Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Order Picked & Packed', status: 'Pending', data: '', comments: '' },
  { name: 'FBA Shipment Created', status: 'Pending', data: '', comments: '' },
  { name: 'Labels Sent to Distributor', status: 'Pending', data: '', comments: '' },
  { name: 'FBA Labels Applied', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Invoice Received', status: 'Pending', data: '', comments: '' },
  { name: 'Shipped to Amazon FBA', status: 'Pending', data: '', comments: '' },
  { name: 'In Transit to Amazon', status: 'Pending', data: '', comments: '' },
  { name: 'Delivered to Amazon Warehouse', status: 'Pending', data: '', comments: '' },
  { name: 'Amazon Receiving in Progress', status: 'Pending', data: '', comments: '' },
  { name: 'Units Received', status: 'Pending', data: '', comments: '' },
  { name: 'Inventory Available for Sale', status: 'Pending', data: '', comments: '' },
  { name: 'Payment to Distributor', status: 'Pending', data: '', comments: '' }
];

// Manufacturing & Production Templates

// Industrial Equipment - International Contract Manufacturing (General cargo)
export const INDUSTRIAL_EQUIPMENT_INTERNATIONAL_TEMPLATE: Task[] = [
  { name: 'Contract Negotiation & Signing', status: 'Pending', data: '', comments: '' },
  { name: 'Technical Specifications Finalized', status: 'Pending', data: '', comments: '' },
  { name: 'Engineering Drawings Approved', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Materials (BOM) Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Production Schedule Agreed', status: 'Pending', data: '', comments: '' },
  { name: 'Down Payment Received', status: 'Pending', data: '', comments: '' },
  { name: 'Raw Materials Procurement', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Control Plan Approved', status: 'Pending', data: '', comments: '' },
  { name: 'Manufacturing Process Started', status: 'Pending', data: '', comments: '' },
  { name: 'First Article Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Production Line Setup Complete', status: 'Pending', data: '', comments: '' },
  { name: 'Bulk Production Started', status: 'Pending', data: '', comments: '' },
  { name: 'In-Process Quality Inspections', status: 'Pending', data: '', comments: '' },
  { name: 'Production Milestone Review', status: 'Pending', data: '', comments: '' },
  { name: 'Final Assembly Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Final Quality Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Performance Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Customer Acceptance Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Packing & Crating', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Forwarder Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Goods Loaded for Export', status: 'Pending', data: '', comments: '' },
  { name: 'Export Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'International Shipping', status: 'Pending', data: '', comments: '' },
  { name: 'Import Clearance at Destination', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery to Customer Site', status: 'Pending', data: '', comments: '' },
  { name: 'Installation & Commissioning', status: 'Pending', data: '', comments: '' },
  { name: 'Training Provided', status: 'Pending', data: '', comments: '' },
  { name: 'Final Payment Received', status: 'Pending', data: '', comments: '' },
  { name: 'Warranty Period Starts', status: 'Pending', data: '', comments: '' }
];

// Chemical Production - Railway Transport
export const CHEMICAL_RAILWAY_TRANSPORT_TEMPLATE: Task[] = [
  { name: 'Production Order Received', status: 'Pending', data: '', comments: '' },
  { name: 'Safety Assessment', status: 'Pending', data: '', comments: '' },
  { name: 'Raw Material Availability Check', status: 'Pending', data: '', comments: '' },
  { name: 'Production Batch Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Reactor Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Process Parameter Setup', status: 'Pending', data: '', comments: '' },
  { name: 'Raw Material Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Chemical Reaction Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Temperature & Pressure Control', status: 'Pending', data: '', comments: '' },
  { name: 'Sample Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Laboratory Analysis', status: 'Pending', data: '', comments: '' },
  { name: 'Process Adjustment', status: 'Pending', data: '', comments: '' },
  { name: 'Reaction Completion', status: 'Pending', data: '', comments: '' },
  { name: 'Product Recovery', status: 'Pending', data: '', comments: '' },
  { name: 'Purification Process', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Control Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Batch Approval', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Product Transfer', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Sealing', status: 'Pending', data: '', comments: '' },
  { name: 'Hazmat Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Railway Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Rail Dispatch', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Unloading Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Return', status: 'Pending', data: '', comments: '' }
];

// Automotive Parts - Container Export
export const AUTOMOTIVE_CONTAINER_EXPORT_TEMPLATE: Task[] = [
  { name: 'Export Order Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Production Scheduling', status: 'Pending', data: '', comments: '' },
  { name: 'Raw Material Allocation', status: 'Pending', data: '', comments: '' },
  { name: 'Manufacturing Process', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Assurance', status: 'Pending', data: '', comments: '' },
  { name: 'Parts Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Export Packaging', status: 'Pending', data: '', comments: '' },
  { name: 'Palletization', status: 'Pending', data: '', comments: '' },
  { name: 'Container Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Certificate of Origin', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Invoice', status: 'Pending', data: '', comments: '' },
  { name: 'Packing List', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Container Sealing', status: 'Pending', data: '', comments: '' },
  { name: 'Export Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Port Transportation', status: 'Pending', data: '', comments: '' },
  { name: 'Container at Port', status: 'Pending', data: '', comments: '' },
  { name: 'Loading on Vessel', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Ocean Transit', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Port Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Import Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container Release', status: 'Pending', data: '', comments: '' },
  { name: 'Final Delivery', status: 'Pending', data: '', comments: '' }
];

// Construction & Engineering - Project-Based Manufacturing (General cargo)
export const CONSTRUCTION_PROJECT_TEMPLATE: Task[] = [
  { name: 'Project Specifications Received', status: 'Pending', data: '', comments: '' },
  { name: 'Feasibility Study', status: 'Pending', data: '', comments: '' },
  { name: 'Cost Estimation', status: 'Pending', data: '', comments: '' },
  { name: 'Contract Award', status: 'Pending', data: '', comments: '' },
  { name: 'Engineering Design', status: 'Pending', data: '', comments: '' },
  { name: 'Structural Calculations', status: 'Pending', data: '', comments: '' },
  { name: 'Material Specifications', status: 'Pending', data: '', comments: '' },
  { name: 'Supplier Selection', status: 'Pending', data: '', comments: '' },
  { name: 'Material Procurement', status: 'Pending', data: '', comments: '' },
  { name: 'Shop Drawing Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Client Approval', status: 'Pending', data: '', comments: '' },
  { name: 'Fabrication Started', status: 'Pending', data: '', comments: '' },
  { name: 'Welding & Assembly', status: 'Pending', data: '', comments: '' },
  { name: 'Non-Destructive Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Surface Treatment', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Assurance', status: 'Pending', data: '', comments: '' },
  { name: 'Dimensional Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Load Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Painting & Coating', status: 'Pending', data: '', comments: '' },
  { name: 'Final Assembly', status: 'Pending', data: '', comments: '' },
  { name: 'Pre-Delivery Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Heavy Haul Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Route Survey', status: 'Pending', data: '', comments: '' },
  { name: 'Permits & Clearances', status: 'Pending', data: '', comments: '' },
  { name: 'Loading & Securing', status: 'Pending', data: '', comments: '' },
  { name: 'Transportation', status: 'Pending', data: '', comments: '' },
  { name: 'Site Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Installation Support', status: 'Pending', data: '', comments: '' },
  { name: 'Project Handover', status: 'Pending', data: '', comments: '' }
];

// Industrial Equipment - Domestic Production (Truck)
export const INDUSTRIAL_DOMESTIC_TRUCK_TEMPLATE: Task[] = [
  { name: 'Customer Order Received', status: 'Pending', data: '', comments: '' },
  { name: 'Product Configuration', status: 'Pending', data: '', comments: '' },
  { name: 'Engineering Review', status: 'Pending', data: '', comments: '' },
  { name: 'Manufacturing Work Order', status: 'Pending', data: '', comments: '' },
  { name: 'Material Requirements Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Procurement Authorization', status: 'Pending', data: '', comments: '' },
  { name: 'Vendor Purchase Orders', status: 'Pending', data: '', comments: '' },
  { name: 'Material Receiving', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Production Scheduling', status: 'Pending', data: '', comments: '' },
  { name: 'Work Order Release', status: 'Pending', data: '', comments: '' },
  { name: 'Machining Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Assembly Process', status: 'Pending', data: '', comments: '' },
  { name: 'Sub-Assembly Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Final Assembly', status: 'Pending', data: '', comments: '' },
  { name: 'Performance Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Certification', status: 'Pending', data: '', comments: '' },
  { name: 'Packaging Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Shipping Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Scheduling', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Dispatch', status: 'Pending', data: '', comments: '' },
  { name: 'In-Transit Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Customer Acceptance', status: 'Pending', data: '', comments: '' }
];

// Bulk Commodities Trading Templates

// Soft Commodities - Bulk Vessel Trading (Grains/Oilseeds)
export const SOFT_COMMODITIES_BULK_TEMPLATE: Task[] = [
  { name: 'Purchase Contract Signed', status: 'Pending', data: '', comments: '' },
  { name: 'Sale Contract Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Letter of Credit Opened', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Nomination', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Acceptance', status: 'Pending', data: '', comments: '' },
  { name: 'Load Port Nomination', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Port Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Laycan Agreed', status: 'Pending', data: '', comments: '' },
  { name: 'Survey Company Appointed', status: 'Pending', data: '', comments: '' },
  { name: 'Insurance Coverage Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival at Load Port', status: 'Pending', data: '', comments: '' },
  { name: 'Pre-Loading Survey', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Operations Started', status: 'Pending', data: '', comments: '' },
  { name: 'Draft Survey - Initial', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Progress Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Draft Survey - Final', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Certificate Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'ETA Updates', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival at Discharge Port', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Survey Arranged', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Final Outturn Survey', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Certificate Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Processing', status: 'Pending', data: '', comments: '' },
  { name: 'Trade Settlement', status: 'Pending', data: '', comments: '' }
];

// Oil & Fuels - Bulk Tanker Trading
export const OIL_FUELS_BULK_TEMPLATE: Task[] = [
  { name: 'Product Purchase Agreement', status: 'Pending', data: '', comments: '' },
  { name: 'Sale Contract Execution', status: 'Pending', data: '', comments: '' },
  { name: 'Credit Terms Established', status: 'Pending', data: '', comments: '' },
  { name: 'Tanker Vessel Nomination', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Vetting Process', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Terminal Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Terminal Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Pipeline Scheduling', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Specifications Confirmed', status: 'Pending', data: '', comments: '' },
  { name: 'Marine Insurance Coverage', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Pre-Arrival Notice', status: 'Pending', data: '', comments: '' },
  { name: 'Port Clearance Obtained', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Berthing', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Commenced', status: 'Pending', data: '', comments: '' },
  { name: 'Product Quality Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Rate Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Completion', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Certificate of Quality/Quantity', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Sailing', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Transit Tracking', status: 'Pending', data: '', comments: '' },
  { name: 'Destination ETA Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Port Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Shore Tank Receipt', status: 'Pending', data: '', comments: '' },
  { name: 'Final Quality Check', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Financial Settlement', status: 'Pending', data: '', comments: '' }
];

// Construction Materials - Bulk Vessel (Cement/Chemicals)
export const CONSTRUCTION_BULK_TEMPLATE: Task[] = [
  { name: 'Supply Contract Agreement', status: 'Pending', data: '', comments: '' },
  { name: 'Purchase Order Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Terms Negotiated', status: 'Pending', data: '', comments: '' },
  { name: 'Bulk Carrier Chartered', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Technical Approval', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Port Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Facility Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Handling Equipment', status: 'Pending', data: '', comments: '' },
  { name: 'Environmental Clearances', status: 'Pending', data: '', comments: '' },
  { name: 'Material Quality Certification', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Arrival & Berthing', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Equipment Setup', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Loading Started', status: 'Pending', data: '', comments: '' },
  { name: 'Dust Control Measures', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Completed', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Sealing', status: 'Pending', data: '', comments: '' },
  { name: 'Shipping Documents Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Voyage Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Weather Updates', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Port ETA', status: 'Pending', data: '', comments: '' },
  { name: 'Pneumatic Discharge Setup', status: 'Pending', data: '', comments: '' },
  { name: 'Unloading Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Material Quality Check', status: 'Pending', data: '', comments: '' },
  { name: 'Quantity Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Storage Facility Transfer', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Settlement', status: 'Pending', data: '', comments: '' }
];

// Metallics & Steel - Bulk Vessel Trading
export const METALLICS_BULK_TEMPLATE: Task[] = [
  { name: 'Metal Purchase Contract', status: 'Pending', data: '', comments: '' },
  { name: 'Grade Specifications Agreed', status: 'Pending', data: '', comments: '' },
  { name: 'Price Fixing Mechanism', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Fixture Negotiations', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Condition Survey', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Port Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Stevedoring Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Survey Appointment', status: 'Pending', data: '', comments: '' },
  { name: 'Marine Insurance Policy', status: 'Pending', data: '', comments: '' },
  { name: 'Mill Test Certificates', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Readiness Notice', status: 'Pending', data: '', comments: '' },
  { name: 'Port Authority Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Equipment Check', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Loading Process', status: 'Pending', data: '', comments: '' },
  { name: 'Hold Trimming Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Scale Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Stability Check', status: 'Pending', data: '', comments: '' },
  { name: 'Departure Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Risk Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Route Optimization', status: 'Pending', data: '', comments: '' },
  { name: 'Arrival Notice Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Port Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Grab Discharge Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Material Segregation', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Testing', status: 'Pending', data: '', comments: '' },
  { name: 'Stockyard Allocation', status: 'Pending', data: '', comments: '' },
  { name: 'Final Weight Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Certificate', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Execution', status: 'Pending', data: '', comments: '' }
];

// Soft Commodities - Container Trading
export const SOFT_COMMODITIES_CONTAINER_TEMPLATE: Task[] = [
  { name: 'Container Load Purchase', status: 'Pending', data: '', comments: '' },
  { name: 'Container Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Certification', status: 'Pending', data: '', comments: '' },
  { name: 'Container Stuffing', status: 'Pending', data: '', comments: '' },
  { name: 'Seal Application', status: 'Pending', data: '', comments: '' },
  { name: 'Container Weighing', status: 'Pending', data: '', comments: '' },
  { name: 'Export Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container to Port', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Issued', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Container Tracking', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Import Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container Release', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery to Warehouse', status: 'Pending', data: '', comments: '' },
  { name: 'Container Destuffing', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Storage Arrangement', status: 'Pending', data: '', comments: '' },
  { name: 'Buyer Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Sample Analysis', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Authorization', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Release', status: 'Pending', data: '', comments: '' }
];

// Oil & Fuels - Railway Tank Car
export const OIL_FUELS_RAILWAY_TEMPLATE: Task[] = [
  { name: 'Rail Transport Contract', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Allocation', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Terminal Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Product Quality Specs', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Pre-Loading Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Product Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Sample Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Sealing', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Rail Movement Order', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Tracking', status: 'Pending', data: '', comments: '' },
  { name: 'Route Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Unloading Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Product Discharge', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Tank Car Cleaning', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Documentation Transfer', status: 'Pending', data: '', comments: '' },
  { name: 'Settlement Process', status: 'Pending', data: '', comments: '' }
];

// Construction Materials - Truck Transport
export const CONSTRUCTION_TRUCK_TEMPLATE: Task[] = [
  { name: 'Truck Load Order', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Site Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Load Securing', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Route Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Schedule', status: 'Pending', data: '', comments: '' },
  { name: 'Site Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Unloading Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Material Discharge', status: 'Pending', data: '', comments: '' },
  { name: 'Quality Check', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Receipt', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Settlement', status: 'Pending', data: '', comments: '' },
  { name: 'Return Logistics', status: 'Pending', data: '', comments: '' }
];

// Metallics & Steel - Container Export
export const METALLICS_CONTAINER_TEMPLATE: Task[] = [
  { name: 'Export Contract Finalized', status: 'Pending', data: '', comments: '' },
  { name: 'Container Reservation', status: 'Pending', data: '', comments: '' },
  { name: 'Mill Certificates Prepared', status: 'Pending', data: '', comments: '' },
  { name: 'Export Packaging', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Distribution', status: 'Pending', data: '', comments: '' },
  { name: 'Container Sealing', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Container Transport', status: 'Pending', data: '', comments: '' },
  { name: 'Port Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Shipping Documents', status: 'Pending', data: '', comments: '' },
  { name: 'Ocean Transit', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Port', status: 'Pending', data: '', comments: '' },
  { name: 'Import Processing', status: 'Pending', data: '', comments: '' },
  { name: 'Container Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Final Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Material Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Completion', status: 'Pending', data: '', comments: '' }
];

// Agency & Forwarding Services Templates

// Container Forwarding - Full Service
export const CONTAINER_FORWARDING_FULL_TEMPLATE: Task[] = [
  { name: 'Forwarding Instruction Received', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Details Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Route Planning & Quotation', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Rate Negotiation', status: 'Pending', data: '', comments: '' },
  { name: 'Booking Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Container Booking with Carrier', status: 'Pending', data: '', comments: '' },
  { name: 'Pick-up Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Export Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Clearance - Export', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loading Supervision', status: 'Pending', data: '', comments: '' },
  { name: 'Seal Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Bill of Lading Processing', status: 'Pending', data: '', comments: '' },
  { name: 'Container Gate-In at Port', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Loading Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Sailing Schedule Update', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Tracking & Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Arrival Notice to Consignee', status: 'Pending', data: '', comments: '' },
  { name: 'Import Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container Release Order', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Container Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Proof of Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Empty Container Return', status: 'Pending', data: '', comments: '' },
  { name: 'Invoice Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Final Settlement', status: 'Pending', data: '', comments: '' }
];

// Container Forwarding - LCL Consolidation
export const CONTAINER_LCL_CONSOLIDATION_TEMPLATE: Task[] = [
  { name: 'LCL Shipment Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Consolidation Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Warehouse Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Inspection', status: 'Pending', data: '', comments: '' },
  { name: 'Measurement & Weighing', status: 'Pending', data: '', comments: '' },
  { name: 'Consolidation Process', status: 'Pending', data: '', comments: '' },
  { name: 'Container Stuffing', status: 'Pending', data: '', comments: '' },
  { name: 'Packing List Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Master Bill of Lading', status: 'Pending', data: '', comments: '' },
  { name: 'House Bill of Lading', status: 'Pending', data: '', comments: '' },
  { name: 'Export Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container to Port', status: 'Pending', data: '', comments: '' },
  { name: 'Vessel Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Ocean Transit', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Port Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Import Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Container to CFS', status: 'Pending', data: '', comments: '' },
  { name: 'De-consolidation', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Segregation', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Notifications', status: 'Pending', data: '', comments: '' },
  { name: 'Individual Deliveries', status: 'Pending', data: '', comments: '' },
  { name: 'Documentation Distribution', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Collection', status: 'Pending', data: '', comments: '' }
];

// Truck Forwarding - Domestic
export const TRUCK_FORWARDING_DOMESTIC_TEMPLATE: Task[] = [
  { name: 'Transport Request Received', status: 'Pending', data: '', comments: '' },
  { name: 'Route Assessment', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Type Selection', status: 'Pending', data: '', comments: '' },
  { name: 'Carrier Selection', status: 'Pending', data: '', comments: '' },
  { name: 'Rate Quotation', status: 'Pending', data: '', comments: '' },
  { name: 'Booking Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Dispatch', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Instructions', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Route Optimization', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Tracking', status: 'Pending', data: '', comments: '' },
  { name: 'ETA Updates', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Unloading Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'POD Collection', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Return', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Invoice', status: 'Pending', data: '', comments: '' },
  { name: 'Payment Processing', status: 'Pending', data: '', comments: '' }
];

// Truck Forwarding - Cross-Border
export const TRUCK_FORWARDING_CROSS_BORDER_TEMPLATE: Task[] = [
  { name: 'Cross-Border Transport Order', status: 'Pending', data: '', comments: '' },
  { name: 'Border Requirements Check', status: 'Pending', data: '', comments: '' },
  { name: 'Documentation Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Truck & Driver Selection', status: 'Pending', data: '', comments: '' },
  { name: 'Insurance Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Export Permits', status: 'Pending', data: '', comments: '' },
  { name: 'Customs Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Supervision', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Securing', status: 'Pending', data: '', comments: '' },
  { name: 'Departure Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Transit to Border', status: 'Pending', data: '', comments: '' },
  { name: 'Export Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Border Crossing', status: 'Pending', data: '', comments: '' },
  { name: 'Import Customs Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Permits', status: 'Pending', data: '', comments: '' },
  { name: 'Route Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Arrival', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Unloading', status: 'Pending', data: '', comments: '' },
  { name: 'Import Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Final Clearance', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Return Journey Planning', status: 'Pending', data: '', comments: '' },
  { name: 'Commercial Settlement', status: 'Pending', data: '', comments: '' }
];

// Railway Forwarding - Domestic Rail
export const RAILWAY_FORWARDING_DOMESTIC_TEMPLATE: Task[] = [
  { name: 'Rail Transport Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Wagon Type Selection', status: 'Pending', data: '', comments: '' },
  { name: 'Railway Operator Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Station Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Station Confirmation', status: 'Pending', data: '', comments: '' },
  { name: 'Transport Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Preparation', status: 'Pending', data: '', comments: '' },
  { name: 'Wagon Positioning', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Operations', status: 'Pending', data: '', comments: '' },
  { name: 'Weight Verification', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Securing', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Wagon Dispatch', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Monitoring', status: 'Pending', data: '', comments: '' },
  { name: 'Schedule Updates', status: 'Pending', data: '', comments: '' },
  { name: 'Arrival Notification', status: 'Pending', data: '', comments: '' },
  { name: 'Discharge Arrangements', status: 'Pending', data: '', comments: '' },
  { name: 'Cargo Unloading', status: 'Pending', data: '', comments: '' },
  { name: 'Delivery Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Final Transport', status: 'Pending', data: '', comments: '' },
  { name: 'Proof of Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Wagon Return', status: 'Pending', data: '', comments: '' },
  { name: 'Freight Settlement', status: 'Pending', data: '', comments: '' }
];

// Railway Forwarding - Intermodal
export const RAILWAY_FORWARDING_INTERMODAL_TEMPLATE: Task[] = [
  { name: 'Intermodal Service Booking', status: 'Pending', data: '', comments: '' },
  { name: 'Container/Trailer Selection', status: 'Pending', data: '', comments: '' },
  { name: 'First Mile Arrangement', status: 'Pending', data: '', comments: '' },
  { name: 'Loading Terminal Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Container Loading', status: 'Pending', data: '', comments: '' },
  { name: 'Rail Terminal Transfer', status: 'Pending', data: '', comments: '' },
  { name: 'Container/Trailer on Rail', status: 'Pending', data: '', comments: '' },
  { name: 'Rail Transit Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Train Departure', status: 'Pending', data: '', comments: '' },
  { name: 'Transit Tracking', status: 'Pending', data: '', comments: '' },
  { name: 'Destination Rail Terminal', status: 'Pending', data: '', comments: '' },
  { name: 'Container/Trailer Off-loading', status: 'Pending', data: '', comments: '' },
  { name: 'Last Mile Coordination', status: 'Pending', data: '', comments: '' },
  { name: 'Truck Dispatch', status: 'Pending', data: '', comments: '' },
  { name: 'Final Delivery', status: 'Pending', data: '', comments: '' },
  { name: 'Container Return', status: 'Pending', data: '', comments: '' },
  { name: 'Multi-Modal Documentation', status: 'Pending', data: '', comments: '' },
  { name: 'Service Completion', status: 'Pending', data: '', comments: '' }
];


